import { mxCell, mxCompactTreeLayout } from 'mxgraph-js';
import FaultTreeMxGraph from '../HCLTreeMxGraph/FaultTreeMxGraph';
import HCLTreeVertex from '../HCLTreeNode/HCLTreeVertex';
import FaultTreeMxGraphDecoder from '../HCLTreeMxGraphDecoder/FaultTreeMxGraphDecoder';
import FaultTreeMxGraphModel from "../HCLMxGraphModel/FaultTreeMxGraphModel";

interface NodeWithDepth {
  depth: number;
  root: HCLTreeVertex;
}

/**
 * Extends <mxGraphLayout> to implement a compact tree (Moen) algorithm. This
 * layout is suitable for graphs that have no cycles (trees). Vertices that are
 * not connected to the tree will be ignored by this layout.
 *
 * @param {FaultTreeMxGraph} graph
 * @param {boolean} horizontal
 * @param {boolean} invert
 *
 * @example
 * var layout = new FaultTreeMxGraphLayout(graph);
 * layout.execute(graph.getDefaultParent());
 */
class FaultTreeMxGraphLayout extends mxCompactTreeLayout {
  private static UNCONNECTED_VERTEX_GAP_X = 150;

  private static UNCONNECTED_VERTEX_GAP_Y = 50; /** gap from bottom of the tree with connected vertices */

  constructor(graph: FaultTreeMxGraph, horizontal = false, invert = false) {
    super(graph, horizontal, invert);
  }

  private static getLeafNodesWithMaxDepth = (current: NodeWithDepth, max: NodeWithDepth): NodeWithDepth => {
    const { root, depth } = current;
    const children = root.getChildren() as HCLTreeVertex[];

    // has no children: root's depth is the maxDepth. Return current
    if (!children || children.length < 1) {
      return current;
    }

    children.forEach((child) => {
      const next: NodeWithDepth = {
        root: child,
        depth: depth + 1,
      };

      const deepestChild: NodeWithDepth = FaultTreeMxGraphLayout.getLeafNodesWithMaxDepth(next, max);

      if (deepestChild.depth > max.depth) {
        max = deepestChild;
      }
    });

    return max;
  };

  /**
   * If the parent has any connected edges, then it is used as the root of
   * the tree. Else, <mxGraph.findTreeRoots> will be used to find a suitable
   * root node within the set of children of the given parent.
   *
   * @param {MxGraphType.mxCell} parent - whose children should be laid out
   * @param {MxGraphType.mxCell} root - optional mxCell that will be used as the root of the tree.
   */
  execute(parent: mxCell, root?: mxCell) {
    const graph = this.getGraph() as FaultTreeMxGraph;

    const topNodes = (graph.getModel() as FaultTreeMxGraphModel).getTopNodes(true);

    const current = {
      root: topNodes[0] as HCLTreeVertex,
      depth: 0,
    };

    const max = {
      root: topNodes[0] as HCLTreeVertex,
      depth: 0,
    };

    const deepestChild: NodeWithDepth = FaultTreeMxGraphLayout.getLeafNodesWithMaxDepth(current, max);
    const toConnectTo = deepestChild.root;

    // layout unconnected vertices
    const unconnectedVertices = topNodes;

    const addedEdges: mxCell[] = [];
    unconnectedVertices.forEach((vertex) => {
      const addedEdge: mxCell = graph.insertEdge(graph.getDefaultParent(), null, null, toConnectTo, vertex);
      // @ts-ignore
      addedEdges.push(addedEdge);
    });

    super.execute(parent, topNodes[0]);

    // @ts-ignore
    graph.removeCells(addedEdges);
  }
}

export default FaultTreeMxGraphLayout;
